<template>
  <div class="workflow" :key="key">
    <div class="d-flex align-items-center workflow__heading">
      <h2 class="textStyle">{{ $t("training.rncp.editing_competency_framework") }}</h2>
    </div>

    <div class="row align-items-center my-4">
      <template v-for="(step, index) in steps" :key="step.id">
        <div class="col-xl-4" :class="{ 'mt-2 mt-xl-0': index > 1 }">
          <div
            class="workflow__step"
            :class="{
              '-success': step.status === rncpConstante.RNCP_STATUS_SUCCESS,
              '-enable': step.status === rncpConstante.RNCP_STATUS_ENABLE,
            }"
          >
            <div class="workflow__step__number">{{ index }}</div>
            <div class="workflow__step__content">
              <div class="workflow__step__content__label" v-if="step.label">{{ step.label }}</div>
              <div class="workflow__step__content__status" v-if="step.statusLabel">{{ step.statusLabel }}</div>
              <div class="workflow__step__content__date" v-if="step.date">{{ step.date }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="row">
      <template v-for="(step, index) in steps" :key="index">
        <div class="col-xl-4" :class="{ 'mt-2 mt-xl-0': index > 1 }">
          <SubmitWithLoaderComponent
            :text="step.buttonText"
            :loading="step.loading"
            :disabled="step.status < rncpConstante.RNCP_STATUS_ENABLE"
            :cfaStyle="true"
            :fullSize="true"
            color="success"
            itemClass="w-100"
            @submit="action(step.action)"
          />
        </div>
      </template>
    </div>
  </div>
  <ModalFormComponent
    :models="models"
    id="trainingCourse-list-modal"
    @submit="publishSubmit"
    @hidden="modalClear"
    @cancel="modalClear"
  />
</template>

<script>
import { dateFormatter } from "@/services/utilities/date-formatter";
import SubmitWithLoaderComponent from "@/components/utilities/SubmitWithLoaderComponent.vue";
import { rncpConstante } from "@/constantes/rncp";
import { i18n } from "@/i18n";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import { trainingManager } from "@/services/training/training-manager";
import { formManager } from "@/services/form/form-manager";

export default {
  name: "TrainingRncpWorkflowComponent",
  components: { ModalFormComponent, SubmitWithLoaderComponent },
  emits: ["import", "enrich", "publish"],
  props: {
    training: {
      type: Object,
      required: true,
    },
    importing: {
      type: Boolean,
      required: true,
    },
    enriching: {
      type: Boolean,
      required: true,
    },
    publishing: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    rncpConstante() {
      return rncpConstante;
    },
    hasBeenImported() {
      return !!this.training.lastSkillImportDate;
    },

    hasBeenEnriched() {
      return !!this.training.lastIAEnrichDate;
    },

    hasBeenPublished() {
      return !!this.training.lastPublishDate;
    },
  },
  data() {
    return {
      key: 0,
      models: [],
      steps: {
        1: {
          id: 1,
          label: i18n.global.t("training.rncp.workflow.skill_import_label"),
          status: rncpConstante.RNCP_STATUS_ENABLE,
          statusLabel: i18n.global.t("training.rncp.workflow.waiting_status_label"),
          date: "",
          buttonText: i18n.global.t("training.rncp.workflow.import_button_label"),
          action: "import",
          loading: false,
        },
        2: {
          id: 2,
          label: i18n.global.t("training.rncp.workflow.enrichment_import_label"),
          status: rncpConstante.RNCP_STATUS_DISABLE,
          statusLabel: i18n.global.t("training.rncp.workflow.waiting_status_label"),
          date: "",
          buttonText: i18n.global.t("training.rncp.workflow.enrichment_button_label"),
          action: "enrich",
          loading: false,
        },
        3: {
          id: 3,
          label: i18n.global.t("training.rncp.workflow.publish_import_label"),
          status: rncpConstante.RNCP_STATUS_DISABLE,
          statusLabel: i18n.global.t("training.rncp.workflow.waiting_status_label"),
          date: "",
          buttonText: i18n.global.t("training.rncp.workflow.publish_button_label"),
          action: "publish",
          loading: false,
        },
      },
    };
  },
  watch: {
    training() {
      this.init();
    },

    importing() {
      this.steps[1].loading = this.importing;
      this.steps[1].statusLabel = i18n.global.t("training.rncp.workflow.pending_status_label");
      this.steps[1].date = null;

      this.steps[2].status = rncpConstante.RNCP_STATUS_DISABLE;
      this.steps[3].status = rncpConstante.RNCP_STATUS_DISABLE;
    },

    enriching() {
      this.steps[2].loading = this.enriching;
      this.steps[2].statusLabel = i18n.global.t("training.rncp.workflow.pending_status_label");
      this.steps[2].date = null;

      this.steps[1].status = rncpConstante.RNCP_STATUS_DISABLE;
      this.steps[3].status = rncpConstante.RNCP_STATUS_DISABLE;
    },

    publishing() {
      this.steps[3].loading = this.publishing;
      this.steps[3].statusLabel = i18n.global.t("training.rncp.workflow.pending_status_label");
      this.steps[3].date = null;

      this.steps[1].status = rncpConstante.RNCP_STATUS_DISABLE;
      this.steps[2].status = rncpConstante.RNCP_STATUS_DISABLE;

      if (!this.publishing) {
        this.modalClear();
      }
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.setProgression();
    },

    setProgression() {
      this.steps[1].statusLabel = i18n.global.t("training.rncp.workflow.skill_import_label");
      this.steps[2].statusLabel = i18n.global.t("training.rncp.workflow.enrichment_import_label");
      this.steps[3].statusLabel = i18n.global.t("training.rncp.workflow.publish_import_label");

      if (!this.training.titleRncp) {
        this.hasNotTitleRncp();
      }
      if (this.training.titleRncp) {
        this.hasTitleRncp();
      }
      if (this.hasBeenImported) {
        this.hasBeenImportedProgress();
      }
      if (this.hasBeenEnriched) {
        this.hasBeenEnrichedProgress();
      }
      if (this.hasBeenPublished) {
        this.hasBeenPublishedProgress();
      }
      this.key++;
    },

    hasNotTitleRncp() {
      this.steps[1].status = rncpConstante.RNCP_STATUS_DISABLE;
      this.steps[1].statusLabel = i18n.global.t("training.rncp.workflow.waiting_status_label");
    },

    hasTitleRncp() {
      this.steps[1].status = rncpConstante.RNCP_STATUS_ENABLE;
    },

    hasBeenImportedProgress() {
      this.steps[1].status = rncpConstante.RNCP_STATUS_SUCCESS;
      this.steps[1].statusLabel = this.importing
        ? i18n.global.t("training.rncp.workflow.pending_status_label")
        : i18n.global.t("training.rncp.workflow.finished_status_label");
      this.steps[1].date = dateFormatter.format(this.training.lastSkillImportDate, "short");
      this.steps[1].loading = this.importing;

      this.steps[2].status = rncpConstante.RNCP_STATUS_ENABLE;
      this.steps[3].status = rncpConstante.RNCP_STATUS_ENABLE;
    },

    hasBeenEnrichedProgress() {
      this.steps[2].status = rncpConstante.RNCP_STATUS_SUCCESS;
      this.steps[2].statusLabel = this.enriching
        ? i18n.global.t("training.rncp.workflow.pending_status_label")
        : i18n.global.t("training.rncp.workflow.finished_status_label");
      this.steps[2].date = this.enriching ? null : dateFormatter.format(this.training.lastIAEnrichDate, "short");
      this.steps[2].loading = this.enriching;

      this.steps[3].status = this.enriching ? rncpConstante.RNCP_STATUS_DISABLE : rncpConstante.RNCP_STATUS_SUCCESS;
    },

    hasBeenPublishedProgress() {
      this.steps[3].status = 3;
      this.steps[3].statusLabel = this.publishing
        ? i18n.global.t("training.rncp.workflow.pending_status_label")
        : i18n.global.t("training.rncp.workflow.finished_status_label");
      this.steps[3].date = this.publishing ? null : dateFormatter.format(this.training.lastPublishDate, "short");

      this.steps[1].status = this.publishing ? rncpConstante.RNCP_STATUS_DISABLE : rncpConstante.RNCP_STATUS_SUCCESS;
      this.steps[2].status = this.publishing ? rncpConstante.RNCP_STATUS_DISABLE : rncpConstante.RNCP_STATUS_SUCCESS;
    },

    action(action) {
      switch (action) {
        case "import":
          this.import();
          break;
        case "enrich":
          this.enrich();
          break;
        case "publish":
          this.publish();
          break;
      }
    },

    import() {
      this.$emit("import");
    },

    enrich() {
      this.$emit("enrich");
    },

    async publish() {
      this.steps[3].loading = true;
      this.models = await trainingManager.form("skill/publish-skills", null, false, this.$route.params.trainingId);
    },

    publishSubmit(models) {
      const params = formManager.processForm(models);
      this.$emit("publish", params);
    },

    modalClear() {
      this.models = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.workflow {
  background: $bg-grey;
  border: 1px solid $light-grey;
  border-radius: $radius;
  padding: 20px;

  @include dark-theme {
    background: $m-color_12;
  }

  &__step {
    display: flex;
    align-items: center;

    &.-success {
      .workflow__step__number {
        background: $green;
      }

      .workflow__step__content__status {
        color: $green;
      }

      .workflow__step__content__label {
        color: var(--primary-color);
        @include dark-theme {
          color: $white;
        }
      }
    }

    &.-enable {
      .workflow__step__number {
        background: $green;
      }
      .workflow__step__content__label {
        color: var(--primary-color);
      }
    }

    &__number {
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 700;
      background: $m_color_4;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      margin-right: 8px;
      color: $white;
    }

    &__content {
      &__label {
        font-weight: 700;
        color: $m_color_4;

        @include dark-theme {
          color: $white;
        }
      }
      &__status,
      &__date {
        font-size: 12px;
        color: $m_color_4;
      }

      &__date {
        color: $green;
      }
    }
  }

  &__button {
    width: 100%;

    &.disabled {
      background: $m_color_4;
      border-color: $m_color_4;
    }
  }
}
</style>
